import { Chart } from "chart.js";
import React, { useEffect, useMemo, useRef, useState } from "react";

// components
import { Spinner } from "@performyard/py-components";

import EmptyChart from "../../../../../../../../components/EmptyChart";
import Tooltip from "../../../../../../../../components/Tooltip";

import { ChartOptions } from "../../../../../../schemas/report";

import useStore from "../../../../store";
import { ChartType } from "../../../../store/enums";

import { getColor } from "../../../../../../../../utils/getColor";
import { useTruncatedText } from "../../../../../../../../utils/textMeasurement/useTruncatedText";
import { getPointTypeFromDataseries } from "../ChartHooks/ScatterChart/utils";

import Scatter from "../ChartHooks/ScatterChart/index";
// hooks
import useScatterData from "../ChartHooks/ScatterChart/useScatterData";
import useChartOptions from "../ChartHooks/UseChartOptions/useChartOptions";
import { useDownloadImage } from "../ChartHooks/UseDownloadImage/useDownloadImage";
import { ChartContainer, VisualizationHeader } from "../Layout";
// styles
import css from "../Layout/Layout.module.scss";
import OptionSettings from "../shared/OptionSettings";

const { dataSeriesRangeOptions } = OptionSettings[ChartType.ScatterChart];

const ScatterChart = () => {
	const chartRef = useRef<Chart>(null);
	const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
	const scaleOptionsByAxis = {
		grid: {
			drawTicks: false,
			color: "#ddd",
		},
		border: {
			color: "#ddd",
			width: 1,
		},
		ticks: {
			padding: 8,
			font: {
				size: 12,
				family: "Source Sans Pro, sans-serif",
			},
		},
	};
	const { dataSeriesOptions, setVisualizeOptions, visualizeOption, previousVisualizeOption, anyAxisOptions } =
		useChartOptions(ChartType.ScatterChart);
	const [enabledSummarizeFields] = useStore((state) => [state.enabledSummarizeFields]);
	const dataSeries = visualizeOption?.dataSeries?.column.split("_");
	const pointsType = getPointTypeFromDataseries(dataSeries);
	const { scatterChartData, count, scaledOptions, axisLabels, isLoading } = useScatterData({
		chartType: ChartType.ScatterChart,
		color: "#fff",
		backgroundColor: pointsType === "group" ? getColor("primary", 5) : undefined,
		activeBorderColor: getColor("primary", 4),
		scaleOptions: {
			x: scaleOptionsByAxis,
			y: scaleOptionsByAxis,
		},
	});

	useEffect(() => {
		setDefaultChartOptions();
	}, []);

	// this is an effect to listen to changes in the axis options
	// it finds the default value for the axis and sets it when the axis option is missing
	useEffect(() => {
		if (
			!anyAxisOptions?.some((option) => option.value === visualizeOption?.xAxis?.column) &&
			visualizeOption?.xAxis?.column
		) {
			setDefaultChartOptions(true, true);
		}

		if (
			!anyAxisOptions?.some((option) => option.value === visualizeOption?.yAxis?.column) &&
			visualizeOption?.yAxis?.column
		) {
			setDefaultChartOptions(true, false);
		}
	}, [anyAxisOptions?.length]);

	const setDefaultChartOptions = (forceUpdate = false, updateXAxis = false) => {
		const options: ChartOptions = {};
		if (dataSeriesOptions.length > 0) {
			const hasRangeOptionCarryover = dataSeriesRangeOptions.some(
				(option) => option.value === previousVisualizeOption?.dataSeries?.rangeOption
			);
			options.dataSeries = {
				column:
					previousVisualizeOption?.dataSeries?.column ||
					visualizeOption?.dataSeries?.column ||
					dataSeriesOptions[dataSeriesOptions.length - 1].value,
				rangeOption: hasRangeOptionCarryover
					? previousVisualizeOption?.dataSeries?.rangeOption
					: visualizeOption?.dataSeries?.rangeOption || dataSeriesRangeOptions[2].value,
			};
		}
		if (anyAxisOptions.length > 1) {
			// handle initial value from carry over
			const carryOverFromX = previousVisualizeOption?.xAxis?.column;
			const carryOverFromY = previousVisualizeOption?.yAxis?.column;
			const carryOverMinFromX = previousVisualizeOption?.xAxis?.min;
			const carryOverMaxFromX = previousVisualizeOption?.xAxis?.max;
			const carryOverMinFromY = previousVisualizeOption?.yAxis?.min;
			const carryOverMaxFromY = previousVisualizeOption?.yAxis?.max;

			const currentXAxisOptions = [...anyAxisOptions];
			const currentYAxisOptions = [...anyAxisOptions];

			let defaultXAxisValue;
			let defaultYAxisValue;

			if (carryOverFromX === undefined && carryOverFromY === undefined) {
				defaultXAxisValue = anyAxisOptions[0].value;
				defaultYAxisValue = anyAxisOptions[1].value;
			}

			if (carryOverFromX) {
				const index = currentYAxisOptions.findIndex((option) => option.value === carryOverFromX);
				currentYAxisOptions.splice(index, 1);
			}

			if (carryOverFromY) {
				const index = currentXAxisOptions.findIndex((option) => option.value === carryOverFromY);
				currentXAxisOptions.splice(index, 1);
			}

			const columnX =
				carryOverFromX || visualizeOption?.xAxis?.column || defaultXAxisValue || currentXAxisOptions[0].value;

			const columnY =
				carryOverFromY || visualizeOption?.yAxis?.column || defaultYAxisValue || currentYAxisOptions[0].value;

			const minSuggestedX = anyAxisOptions.find((option) => option.value === columnX)?.metadata?.min;
			const maxSuggestedX = anyAxisOptions.find((option) => option.value === columnX)?.metadata?.max;
			const minSuggestedY = anyAxisOptions.find((option) => option.value === columnY)?.metadata?.min;
			const maxSuggestedY = anyAxisOptions.find((option) => option.value === columnY)?.metadata?.max;

			options.xAxis = {
				column: columnX,
				min: carryOverMinFromX || minSuggestedX || 0,
				max: carryOverMaxFromX || maxSuggestedX || 100,
				hasSuggestedMin: !!carryOverMinFromX || !!minSuggestedX,
				hasSuggestedMax: !!carryOverMaxFromY || !!maxSuggestedX,
			};

			options.yAxis = {
				column: columnY,
				min: carryOverMinFromY || minSuggestedY || 0,
				max: carryOverMaxFromY || maxSuggestedY || 100,
				hasSuggestedMin: !!carryOverMinFromY || !!minSuggestedY,
				hasSuggestedMax: !!carryOverMaxFromY || !!maxSuggestedY,
			};

			if (forceUpdate && updateXAxis) {
				options.xAxis.column = defaultXAxisValue || currentXAxisOptions[0].value;
			}

			if (forceUpdate && !updateXAxis) {
				options.yAxis.column = defaultYAxisValue || currentYAxisOptions[0].value;
			}
		}

		// Validate selections still exist in available options
		if (options.dataSeries && !dataSeriesOptions.some((o) => o.value === options.dataSeries?.column)) {
			options.dataSeries = {
				column: dataSeriesOptions[dataSeriesOptions.length - 1].value,
				rangeOption: dataSeriesRangeOptions[2].value,
			};
		}
		if (options.xAxis && !anyAxisOptions.some((o) => o.value === options.xAxis?.column)) {
			options.xAxis = { column: anyAxisOptions[0].value };
		}
		if (options.yAxis && !anyAxisOptions.some((o) => o.value === options.yAxis?.column)) {
			options.yAxis = { column: anyAxisOptions[1].value };
		}

		// Apply carryover → saved → defaults, then persist
		const newVisualizeOptions = { [ChartType.ScatterChart]: { ...visualizeOption, ...options } };
		setVisualizeOptions(newVisualizeOptions);
	};

	useEffect(() => {
		// Observe size changes
		const resizeObserver = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;
			requestAnimationFrame(() => {
				setCanvasDimensions({ width, height });
			});
		});

		if (chartRef.current) {
			resizeObserver.observe(chartRef.current.canvas);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [axisLabels.xAxisLabel, axisLabels.yAxisLabel, chartRef.current]);

	const formattedDescription = useMemo(() => {
		if (!scatterChartData || !count) {
			return "";
		}
		const numberOf = scatterChartData.datasets[0]?.data?.length;
		const dataSeriesOption = enabledSummarizeFields.find(
			(field) => field.value === visualizeOption?.dataSeries?.column.slice(8)
		)?.label;
		return `This chart is displaying ${numberOf} of ${count} ${
			dataSeriesOption ? `${dataSeriesOption} ` : ""
		}Items.`;
	}, [JSON.stringify(scatterChartData), count]);

	const { downloadImage } = useDownloadImage(chartRef, {
		chartType: ChartType.ScatterChart,
		description: formattedDescription,
		xAxisLabel: `${axisLabels.xAxisLabel} ${axisLabels.xAggregateLabel}`,
		yAxisLabel: `${axisLabels.yAxisLabel} ${axisLabels.yAggregateLabel}`,
	});

	const { didTruncateText: didTruncateXLabel } = useTruncatedText(
		axisLabels.xAxisLabel || "",
		canvasDimensions.width * 0.7 || 500,
		"paragraph-2",
		"regular"
	);
	const { didTruncateText: didTruncateYLabel } = useTruncatedText(
		axisLabels.yAxisLabel || "",
		canvasDimensions.height * 0.7 || 500,
		"paragraph-2",
		"regular"
	);

	const Tooltips = (
		<>
			{didTruncateXLabel && (
				<div className={css.scatterXTooltip}>
					<Tooltip message={axisLabels.xAxisLabel || ""}>
						<div className={css.tooltipTextPlaceholder} style={{ width: canvasDimensions.width * 0.7 }} />
					</Tooltip>
				</div>
			)}
			{didTruncateYLabel && (
				<div className={css.scatterYTooltip}>
					<Tooltip message={axisLabels.yAxisLabel || ""}>
						<div
							className={css.tooltipVerticalTextPlacholder}
							style={{ height: canvasDimensions.height * 0.7 }}
						/>
					</Tooltip>
				</div>
			)}
		</>
	);

	return (
		<ChartContainer padding="16px 0px" height="calc(100% - 72px)" width="calc(100% - 4px)">
			{isLoading && !scatterChartData.datasets[0].data.length && (
				<div className={css.spinner}>
					<Spinner size={20} />
				</div>
			)}
			{!isLoading && scatterChartData.datasets[0].data.length > 0 && (
				<>
					<VisualizationHeader
						viewingCount={scatterChartData?.datasets[0]?.data?.length}
						totalCount={count}
						options={[
							{
								id: "download-image",
								label: "Download Image",
								action: downloadImage,
							},
						]}
					/>
					<div className={css.relativeContainer}>
						<Scatter
							key={axisLabels.xAxisLabel + axisLabels.yAxisLabel}
							ref={chartRef}
							data={scatterChartData}
							options={scaledOptions}
							dataTestId="scatterChart"
						/>
						{/* Tooltips */}
						{Tooltips}
					</div>
				</>
			)}
			{!isLoading && scatterChartData.datasets[0].data.length === 0 && (
				<EmptyChart type={ChartType.ScatterChart} />
			)}
		</ChartContainer>
	);
};

export default ScatterChart;
